import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

const SKEW = 9; // parallelogram skew in degrees
const CLUSTER_H = 360;
const SRC_LOGO =
  "https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/x1pnllq7_SRC%20Web%20icon%20%282%29.png";
const CARD_H = 320;
const GAP = 18; // grey extension length between tile and card
const HUB_GREY = "#e2e3e6"; // selected tile + card + extension
const STAGE_H = CLUSTER_H + CARD_H + GAP; // fixed stage height
const SHIFT = Math.tan((SKEW * Math.PI) / 180) * (CLUSTER_H / 2); // skew offset at the cluster edge

const slug = (t) => t.toLowerCase().replace(/\s|&/g, "-");

function TileContent({ f }) {
  const Icon = f.icon;
  return (
    <div
      className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2.5 px-3 text-center"
      style={{ transform: `skewX(${SKEW}deg)` }}
    >
      <Icon
        className="h-7 w-7 text-[#0b0b0d]"
        strokeWidth={1.9}
      />
      <span
        className="font-display text-[1.4rem] font-bold uppercase leading-tight tracking-wide sm:text-[1.65rem]"
        style={{ color: "#0b0b0d", textShadow: "0 1px 0 rgba(255,255,255,0.8)" }}
      >
        {f.title}
      </span>
    </div>
  );
}

function Cluster({ items, active, onSelect }) {
  return (
    <div className="relative h-full w-full" style={{ transform: `skewX(-${SKEW}deg)` }}>
      <div className="grid h-full w-full grid-cols-3 grid-rows-2 gap-[6px]">
        {items.map((f, i) => (
          <button
            key={f.title}
            type="button"
            onClick={() => onSelect(active === i ? null : i)}
            data-testid={`feature-hub-tile-${slug(f.title)}`}
            aria-pressed={active === i}
            className={`relative overflow-hidden rounded-[6px] shadow-[0_14px_28px_-12px_rgba(0,0,0,0.4)] transition-colors duration-300 ${
              active === i ? "" : "bg-white hover:bg-[#f0f0f2]"
            }`}
            style={active === i ? { backgroundColor: HUB_GREY } : undefined}
          >
            {/* red left accent (matches /learn feature-card tabs) */}
            <span aria-hidden className="absolute inset-y-0 left-0 w-2.5 bg-primary" />
            <TileContent f={f} />
          </button>
        ))}
      </div>

      {/* Center SRC logo — overlaps the middle seam, taps pass through */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
        <img
          src={SRC_LOGO}
          alt="Sim Racing Connected"
          draggable={false}
          className="h-16 w-auto select-none object-contain sm:h-[4.5rem]"
          style={{ transform: `skewX(${SKEW}deg)`, filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.5))" }}
        />
      </div>

      {/* grey extension — shares the tile's column & skew, bridging it to the card.
          Rendered after the tiles so it covers their drop shadow at the junction; the
          card sits above it (higher z), so the card's red perimeter stays unbroken and
          its junction shadow falls onto this tab. */}
      {active !== null && (
        <div
          aria-hidden
          className="absolute z-20"
          style={{
            backgroundColor: HUB_GREY,
            left: `calc(${active % 3} * ((100% - 12px) / 3 + 6px) + 10px)`,
            width: "calc((100% - 12px) / 3 - 10px)",
            height: GAP + 6,
            ...(active < 3 ? { top: -(GAP + 4) } : { bottom: -(GAP + 4) }),
          }}
        />
      )}
    </div>
  );
}

function OpenCard({ f, onClose, isTop }) {
  const Icon = f.icon;
  // ambient float + a focused junction shadow the card casts onto the tab:
  // top-row card casts down onto the tab below it; bottom-row card casts up onto the tab above it.
  const junction = isTop
    ? "0 10px 16px -8px rgba(0,0,0,0.28)"
    : "0 -10px 16px -8px rgba(0,0,0,0.28)";
  return (
    <div
      key={f.title}
      className="animate-fade-up relative grid h-full grid-cols-2 gap-6 overflow-hidden rounded-lg border-2 border-primary p-6"
      style={{ backgroundColor: HUB_GREY, boxShadow: `0 24px 60px -24px rgba(0,0,0,0.5), ${junction}` }}
      data-testid="feature-hub-card"
    >
      <div className="relative overflow-hidden rounded-md">
        <img src={f.img} alt={f.title} className="h-full w-full object-cover" />
        <div className="pointer-events-none absolute inset-0 bg-black/15" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      </div>
      <div className="flex flex-col justify-center pr-6">
        <span className="flex h-11 w-11 items-center justify-center rounded-md border border-black/10 bg-white">
          <Icon className="h-5 w-5 text-primary" />
        </span>
        <h3 className="mt-4 font-display text-2xl font-bold uppercase leading-tight sm:text-3xl" style={{ color: "#0b0b0d" }}>{f.title}</h3>
        <p className="mt-3 text-base leading-relaxed" style={{ color: "#3f3f46" }}>{f.desc}</p>
        <Link
          to={f.link}
          className="mt-5 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary transition-colors hover:text-primary/80"
          data-testid={`feature-hub-link-${slug(f.title)}`}
        >
          Explore {f.title} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="feature-hub-close"
        className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-md border border-black/10 bg-white/80 text-[#0b0b0d]/60 transition-colors hover:text-[#0b0b0d]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function FeatureHub({ items }) {
  const [active, setActive] = useState(0);
  const isTop = active !== null && active < 3;

  const clusterTop =
    active === null ? (STAGE_H - CLUSTER_H) / 2 : isTop ? STAGE_H - CLUSTER_H : 0;
  const cardTop = isTop ? clusterTop - GAP - CARD_H : clusterTop + CLUSTER_H + GAP;

  const transition = "top 550ms cubic-bezier(0.22, 1, 0.36, 1)";

  return (
    <div
      className="relative mx-auto w-full max-w-[860px]"
      style={{ height: STAGE_H }}
      data-testid="feature-hub"
    >
      {active !== null && (
        /* open card — sits above the cluster/tab so its red perimeter is unbroken */
        <div
          className="absolute left-0 right-0 z-40"
          style={{
            top: cardTop,
            height: CARD_H,
            transform: `translateX(${isTop ? SHIFT + 14 : -SHIFT}px)`,
            transition,
          }}
        >
          <OpenCard f={items[active]} onClose={() => setActive(null)} isTop={isTop} />
        </div>
      )}

      {/* the red cluster (its grey extension bridges the active tile to the card) */}
      <div
        className="absolute left-0 right-0 z-30"
        style={{ top: clusterTop, height: CLUSTER_H, transition }}
      >
        <Cluster items={items} active={active} onSelect={setActive} />
      </div>
    </div>
  );
}
